import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client, { endpoints, errText } from '../api/client';
import { money } from '../lib/format';

export default function Admin() {
  const [summary, setSummary] = useState(null);
  const [setup, setSetup] = useState(null);
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setErr('');
    try {
      const [s, a] = await Promise.all([
        client.get(endpoints.analytics.summary),
        client.get(endpoints.analytics.activity, { params: { limit: 20 } }),
      ]);
      setSummary(s.data);
      setActivity(a.data.items);
    } catch (e) { setErr(errText(e)); }
    // Setup is admin-only; a manager sees the rest of the page without it.
    client.get(endpoints.admin.setup).then((r) => setSetup(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      const { data } = await client.post(endpoints.admin.seed);
      const added = [...data.created.areas, ...data.created.services];
      setMsg(added.length ? `Added: ${added.join(', ')}.` : 'Nothing to add — the catalogue and cities are already in place.');
      load();
    } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  return (
    <section className="section">
      <h1>Admin</h1>
      {err && <p className="alert alert-err">{err}</p>}
      {msg && <p className="alert alert-ok">{msg}</p>}

      {setup && (
        <div className={`card ${setup.ready ? '' : 'card-warn'}`}>
          <h2>Setup {setup.ready ? '— complete' : '— unfinished'}</h2>
          <ul className="setup-list">
            {setup.steps.map((s) => (
              <li key={s.key} className={s.done ? 'done' : ''}>
                <strong>{s.done ? '✓' : '•'} {s.label}</strong> <span className="muted">— {s.detail}</span>
                {!s.done && <div className="setup-blocks">{s.blocks}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary && (
        <>
          <div className="card">
            <h2>Money <span className="muted">({summary.range.replace(/_/g, ' ')})</span></h2>
            <div className="stat-row">
              <div className="stat"><strong>{summary.money.bookedLabel}</strong><span>booked</span></div>
              <div className="stat"><strong>{summary.money.collectedLabel}</strong><span>collected</span></div>
              <div className="stat due"><strong>{summary.money.depositsDueLabel}</strong><span>deposits outstanding</span></div>
              <div className="stat due"><strong>{summary.money.balancesDueLabel}</strong><span>balances outstanding</span></div>
            </div>
            <p className="muted small">
              &ldquo;Collected&rdquo; counts only deposits and balances actually marked paid — a booked job that has not paid
              is not revenue.
            </p>
          </div>

          <div className="card">
            <h2>Work</h2>
            <div className="stat-row">
              <div className="stat"><strong>{summary.today.jobs}</strong><span>jobs today</span></div>
              <div className="stat"><strong>{summary.bookings.total}</strong><span>bookings in range</span></div>
              <div className="stat"><strong>{summary.quotes.winRate == null ? '—' : `${summary.quotes.winRate}%`}</strong><span>quote win rate</span></div>
              <div className="stat"><strong>{summary.reviews.average ?? '—'}</strong><span>avg rating ({summary.reviews.count})</span></div>
            </div>
            {summary.byService.length > 0 && (
              <table className="table table-tight">
                <thead><tr><th>Service</th><th>Bookings</th><th>Value</th></tr></thead>
                <tbody>
                  {summary.byService.map((s) => (
                    <tr key={s.serviceKey}><td>{s.serviceKey}</td><td>{s.bookings}</td><td>{money(s.valueMinor)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
            {summary.waitlistTopCities.length > 0 && (
              <p className="muted small">
                Most requested cities you do not cover: {summary.waitlistTopCities.map((c) => `${c.city} (${c.requests})`).join(', ')}.
              </p>
            )}
          </div>
        </>
      )}

      <div className="card">
        <h2>Quick navigation</h2>
        <ul className="link-list">
          <li><Link to="/ops/services">→ Services &amp; prices</Link></li>
          <li><Link to="/ops/teams">→ Teams</Link></li>
          <li><Link to="/ops/coverage">→ Coverage &amp; waitlist</Link></li>
          <li><Link to="/admin/users">→ Users</Link></li>
          <li><Link to="/admin/invites">→ Invites</Link></li>
          <li><Link to="/admin/roles">→ Roles &amp; permissions</Link></li>
          <li><Link to="/account/api-keys">→ Your API keys</Link></li>
        </ul>
      </div>

      <div className="card">
        <h2>Catalogue</h2>
        <p className="muted">
          Re-adds any missing service or city from the original build. It never overwrites a price, checklist or
          description you have edited.
        </p>
        <button className="btn-ghost" onClick={seed} disabled={busy}>{busy ? 'Working…' : 'Restore missing services & cities'}</button>
      </div>

      <div className="card">
        <h2>Recent activity</h2>
        <table className="table table-tight">
          <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Target</th></tr></thead>
          <tbody>
            {activity.map((a) => (
              <tr key={a._id}>
                <td>{new Date(a.createdAt).toLocaleString('en-GB')}</td>
                <td>{a.actor?.name || a.actor?.email || 'public'}</td>
                <td>{a.action}</td>
                <td>{a.target} {a.targetId ? String(a.targetId).slice(-6) : ''}</td>
              </tr>
            ))}
            {activity.length === 0 && <tr><td colSpan={4} className="muted">Nothing yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
